import c from 'classnames';
import React from 'react';

import { Spinfinity, TextColor, Typography } from '@repo/ui';

import illustration from 'assets/images/summary/first-look/actionplan_illustration.svg';
import { useProcessedActionPlanData } from '../hooks/useProcessedActionPlanData';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';

import { AgeGroup, getAgeGroup } from '@repo/utils';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import removeYourFromLabel from '../helpers/removeYourFromLabel';
import styles from './ActionPlan.module.css';
import slideStyles from './Slides.module.css';

const ActionPlanItem = ({ index, title }: { index: number; title: string }) => {
  return (
    <div className={styles.actionPlanItem}>
      <div className={styles.circle}>
        <Typography variant='heading-m'>{index}</Typography>
      </div>
      <Typography variant='heading-m'>{title}</Typography>
    </div>
  );
};

interface ActionPlanProps {
  /** Adds the retest step, numbered after the action plan's own steps. */
  showRetestStep?: boolean;
}

export const ActionPlan: React.FC<ActionPlanProps> = ({
  showRetestStep = false,
}) => {
  const { steps } = useProcessedActionPlanData();

  const { currentKit, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const ageGroup =
    currentKit?.age_at_sampling !== null &&
    currentKit?.age_at_sampling !== undefined
      ? getAgeGroup(currentKit.age_at_sampling)
      : undefined;
  const isAdult = ageGroup === AgeGroup.ADULT;
  const name = currentTinyAccount?.first_name ?? '';

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slideContainer)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlan }}
    >
      <div className={styles.top}>
        <Typography
          variant='heading-2xl'
          color={TextColor.DARKORANGE}
          weight='medium'
        >
          Here's our Action Plan for {isAdult ? 'you' : name}:
        </Typography>
        <div className={styles.actionPlanItems}>
          {steps.length > 0 ? (
            <>
              {steps.map((step, index) => (
                <ActionPlanItem
                  key={step.stepNumber}
                  index={index + 1}
                  title={isAdult ? step.title : removeYourFromLabel(step.title)}
                />
              ))}
              {showRetestStep && (
                <ActionPlanItem
                  index={steps.length + 1}
                  title='Retest to monitor changes'
                />
              )}
            </>
          ) : (
            <div className={styles.loadingContainer}>
              <Spinfinity
                color='var(--orange-300)'
                secondaryColor='var(--orange-100)'
              />
            </div>
          )}
        </div>
      </div>
      <div className={c(styles.bottom, styles.background)}>
        <img
          src={illustration}
          alt='Action Plan Illustration'
          className={styles.illustration}
        />
      </div>
    </div>
  );
};
